import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Box, { type BoxProps } from '@mui/material/Box'
import Table, { type TableProps } from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import {
  flexRender,
  type Cell,
  type Header,
  type HeaderGroup,
  type Row,
  type Table as TableType,
} from '@tanstack/react-table'
import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual'
import classNames from 'classnames/bind'
import { Fragment, type CSSProperties, type PropsWithChildren, type ReactElement, type RefObject } from 'react'
import styles from './DataTableBase.module.scss'
import { DataTablePagination, type DataTablePaginationProps } from './DataTablePagination'

import { TableRowWithCenterElement } from './TableRowWithCenterElement'
const cx = classNames.bind(styles)

const Placeholder = (props: PropsWithChildren<BoxProps>) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  />
)

export interface DataTableCellContext<TData> {
  cell: Cell<TData, unknown>
  row: Row<TData>
}

export interface DataTableBaseProps<TData = unknown> {
  table: TableType<TData>
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>
  containerRef?: RefObject<HTMLDivElement | null>

  className?: TableProps['className']
  style?: TableProps['style']
  isEmpty?: boolean
  isLoading?: boolean
  maxHeight?: number | string
  maxWidth?: number | string
  width?: number | string
  pagination?: Omit<DataTablePaginationProps<TData>, 'table' | 'isVirtualized'>
  renderSubComponent?: (props: { row: Row<TData> }) => ReactElement
  renderNoDataComponent?: () => ReactElement
  renderLoadingComponent?: () => ReactElement
  renderPaginationComponent?: (props: DataTablePaginationProps<TData>) => ReactElement | null
  isWithVerticalGridlines?: boolean
  getCellProps?: (context: DataTableCellContext<TData>) => React.ComponentProps<typeof TableCell>

  // DnD props
  isDraggable?: boolean
  onDragEnd?: (event: DragEndEvent) => void
  onDragStart?: (event: DragStartEvent) => void
  getRowDraggableId?: (row: Row<TData>) => string
}

interface DraggableRowProps<TData> {
  row: Row<TData>
  draggableId: string
  isWithVerticalGridlines: boolean
  getCellProps?: (context: DataTableCellContext<TData>) => React.ComponentProps<typeof TableCell>
  renderSubComponent?: (props: { row: Row<TData> }) => ReactElement
}

const DraggableRow = <TData,>({
  row,
  draggableId,
  isWithVerticalGridlines,
  getCellProps,
  renderSubComponent,
}: DraggableRowProps<TData>) => {
  const { transform, transition, setNodeRef, isDragging, attributes, listeners } = useSortable({
    id: draggableId,
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  }

  const renderDraggableCell = (cell: Cell<TData, unknown>) => {
    const { cellProps } = cell.column.columnDef.meta || {}
    const dynamicCellProps = getCellProps?.({ cell, row: cell.row })
    const isDragColumn = cell.column.id === 'drag'

    const cellStyle: CSSProperties = {
      width: cell.column.getSize(),
      ...dynamicCellProps?.style,
      ...(isDragColumn ? { cursor: 'grab' } : {}),
    }

    return (
      <TableCell
        key={cell.id}
        className={isWithVerticalGridlines ? cx('table-cell') : undefined}
        {...cellProps}
        {...dynamicCellProps}
        {...(isDragColumn ? { ...attributes, ...listeners } : {})}
        style={cellStyle}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    )
  }

  return (
    <Fragment>
      <TableRow
        ref={setNodeRef}
        style={style}
        className={cx('table-row', { 'is-dragging': isDragging })}
      >
        {row.getVisibleCells().map(renderDraggableCell)}
      </TableRow>
      {row.getIsExpanded() && renderSubComponent?.({ row })}
    </Fragment>
  )
}

export const DataTableBase = <TData, >(props: DataTableBaseProps<TData>) => {
  const {
    table,
    containerRef,
    className,
    style,
    isEmpty,
    isLoading,
    maxHeight,
    maxWidth,
    width,
    pagination,
    rowVirtualizer,
    renderSubComponent,
    isWithVerticalGridlines = true,
    renderNoDataComponent = () => <Placeholder>無資料</Placeholder>,
    renderLoadingComponent = () => <Placeholder>載入中...</Placeholder>,
    renderPaginationComponent = paginationProps => (
      <DataTablePagination
        {...paginationProps}
        table={table}
        isVirtualized={!!rowVirtualizer}
      />
    ),
    isDraggable,
    onDragEnd,
    onDragStart,
    getRowDraggableId,
  } = props

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  )

  const renderPagination = () => {
    if (!pagination) return null
    return renderPaginationComponent({
      ...pagination,
      table,
      isVirtualized: !!rowVirtualizer,
    })
  }

  const renderHeader = (header: Header<TData, unknown>) => {
    const { cellProps, isSortable } = header.column.columnDef.meta || {}
    const { colSpan, rowSpan, style, ...restCellProps } = cellProps || {}

    const label = flexRender(header.column.columnDef.header, header.getContext())
    const renderHeaderLabel = () => {
      if (header.isPlaceholder) return null
      if (isSortable) {
        return (
          <TableSortLabel
            onDoubleClick={() => header.column.resetSize()}
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            onClick={header.column.getToggleSortingHandler()}
            direction={header.column.getIsSorted() || undefined}
          >
            {label}
            <div
              {...{
                onDoubleClick: () => header.column.resetSize(),
                onMouseDown: header.getResizeHandler(),
                onTouchStart: header.getResizeHandler(),
                className: cx('resizer',
                  table.options.columnResizeDirection,
                  {
                    isResizing: header.column.getIsResizing(),
                  },
                ),
              }}
            />
          </TableSortLabel>
        )
      }
      return (
        <>
          {label}
          <div
            {...{
              onDoubleClick: () => header.column.resetSize(),
              onMouseDown: header.getResizeHandler(),
              onTouchStart: header.getResizeHandler(),
              className: cx('resizer',
                table.options.columnResizeDirection,
                {
                  isResizing: header.column.getIsResizing(),
                },
              ),
            }}
          />
        </>
      )
    }

    return (
      <TableCell
        key={header.id}
        className={isWithVerticalGridlines ? cx('table-cell') : undefined}
        colSpan={colSpan ?? header.colSpan}
        rowSpan={rowSpan ?? header.rowSpan}
        style={{ width: `${header.getSize()}px`, ...style }}
        {...restCellProps}
      >
        {renderHeaderLabel()}
      </TableCell>
    )
  }

  const renderHeaderGroup = (headerGroup: HeaderGroup<TData>) => (
    <TableRow key={headerGroup.id} className={cx('table-row')}>
      {headerGroup.headers.map(renderHeader)}
    </TableRow>
  )

  const renderCell = (cell: Cell<TData, unknown>) => {
    const { cellProps } = cell.column.columnDef.meta || {}
    const dynamicCellProps = props.getCellProps?.({ cell, row: cell.row })
    return (
      <TableCell
        key={cell.id}
        className={isWithVerticalGridlines ? cx('table-cell') : undefined}
        style={{ width: cell.column.getSize() }}
        {...cellProps}
        {...dynamicCellProps}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    )
  }

  const renderRow = (row: Row<TData>, virtualRow?: VirtualItem) => {
    const virtualProps
      = virtualRow && rowVirtualizer
        ? {
          'data-index': row.index,
          'ref': (node: HTMLTableRowElement) => rowVirtualizer.measureElement(node),

          'style': {
            transform: `translateY(${virtualRow.start}px)`,
            position: 'absolute',
          } as CSSProperties,
        }
        : {}
    return (
      <Fragment key={row.id}>
        <TableRow className={cx('table-row')} {...virtualProps}>
          {row.getVisibleCells().map(renderCell)}
        </TableRow>
        {row.getIsExpanded() && renderSubComponent?.({ row })}
      </Fragment>
    )
  }

  const renderRows = () => {
    const { rows } = table.getRowModel()
    const { pageSize } = table.getState().pagination

    if (isLoading) {
      // When the table per page is, say, 1000 rows, the row count has to be small enough to allow the user to see the loading icon.
      const loadingSize = 3
      return <TableRowWithCenterElement className={cx('table-row')} rowSize={loadingSize}>{renderLoadingComponent()}</TableRowWithCenterElement>
    }
    if (isEmpty) {
      return <TableRowWithCenterElement className={cx('table-row')} rowSize={pageSize}>{renderNoDataComponent()}</TableRowWithCenterElement>
    }
    if (rowVirtualizer) {
      return rowVirtualizer
        .getVirtualItems()
        // @ts-expect-error - TODO: fix this
        .map(virtualRow => renderRow(rows[virtualRow.index], virtualRow))
    }
    return rows.map(row => renderRow(row))
  }

  const renderDraggableRows = () => {
    const { rows } = table.getRowModel()
    const { pageSize } = table.getState().pagination

    if (isLoading) {
      const loadingSize = 3
      return <TableRowWithCenterElement className={cx('table-row')} rowSize={loadingSize}>{renderLoadingComponent()}</TableRowWithCenterElement>
    }
    if (isEmpty) {
      return <TableRowWithCenterElement className={cx('table-row')} rowSize={pageSize}>{renderNoDataComponent()}</TableRowWithCenterElement>
    }

    const rowIds = rows.map(row => getRowDraggableId?.(row) ?? row.id)

    return (
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        {rows.map((row) => {
          const draggableId = getRowDraggableId?.(row) ?? row.id
          return (
            <DraggableRow
              key={draggableId}
              draggableId={draggableId}
              row={row}
              isWithVerticalGridlines={isWithVerticalGridlines}
              getCellProps={props.getCellProps}
              renderSubComponent={renderSubComponent}
            />
          )
        })}
      </SortableContext>
    )
  }

  const renderTableBody = () => {
    if (isDraggable) {
      return (
        <TableBody sx={{ position: 'relative' }}>
          {renderDraggableRows()}
        </TableBody>
      )
    }

    return (
      <TableBody
        sx={{ position: 'relative' }}
        style={{
          height: rowVirtualizer ? `${rowVirtualizer.getTotalSize()}px` : undefined,
        }}
      >
        {renderRows()}
      </TableBody>
    )
  }

  const tableElement = (
    <Table>
      <TableHead
        className={cx('table-head')}
        sx={theme => ({
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[600],
          fontWeight: 600,
        })}
      >
        {table.getHeaderGroups().map(renderHeaderGroup)}
      </TableHead>
      {renderTableBody()}
    </Table>
  )

  const renderTableElement = () => {
    if (!isDraggable) return tableElement

    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        sensors={sensors}
      >
        {tableElement}
      </DndContext>
    )
  }

  return (
    <>
      <div className={cx('root')} style={{ direction: table.options.columnResizeDirection }}>
        <div
          className={cx('table', rowVirtualizer && 'is-virtualized', className)}
          style={{ maxHeight, maxWidth, width, ...style }}
          ref={containerRef}
        >
          {renderTableElement()}
        </div>
      </div>
      {renderPagination()}
    </>
  )
}
